import { useState } from "react";
import { FileText, Mail, MessageSquare, Stethoscope } from "lucide-react";
import { PatientsSmsTable } from "pages/patient/PatientCommunicationPage/components/PatientSms/PatientSms";

import { Loader } from "components/Loader/Loader";
import { Dialog } from "components/ui/Dialog";
import { PageHeader } from "components/ui/PageHeader";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { CommunicationsTable } from "features/communications/components/CommunicationsTable/CommunicationsTable";
import { LettersSection } from "features/communications/components/LettersSection/LettersSection";
import { SickCertsSection } from "features/communications/components/SickCertsSection/SickCertsSection";
import { usePracticeSmsHistory } from "features/communications/hooks/usePracticeSmsHistory";
import { SmsModal2 } from "features/comunications/components/SmsModal2/SmsModal2";
import { SelectPatientDialog } from "features/patients/components/SelectPatientDialog/SelectPatientDialog";
import { usePatientData } from "features/patients/hooks/usePatientData";

import styles from "./CommunicationsPage.module.css";

const TABS = {
  SMS: "SMS",
  EMAIL: "EMAIL",
  LETTERS: "LETTERS",
  SICK_CERTS: "SICK_CERTS",
};

const TAB_OPTIONS = [
  { value: TABS.SMS, label: "SMS", icon: <MessageSquare size={16} /> },
  { value: TABS.EMAIL, label: "Email", icon: <Mail size={16} /> },
  { value: TABS.LETTERS, label: "Letters", icon: <FileText size={16} /> },
  {
    value: TABS.SICK_CERTS,
    label: "Sick Certs",
    icon: <Stethoscope size={16} />,
  },
];

/*
 * The same four lists the patient's Communications tab has, but practice-wide: each
 * section is the patient-page component rendered with no patientId, which is what puts
 * it into its tenant-wide mode (a Patient column, and no per-patient actions).
 */
const CommunicationsPage = () => {
  const [activeTab, setActiveTab] = useState(TABS.SMS);
  const [isPickingSmsPatient, setIsPickingSmsPatient] = useState(false);
  const [smsPatientId, setSmsPatientId] = useState(null);

  const {
    data: smsData,
    isLoading: isLoadingSms,
    refetch: refetchSms,
  } = usePracticeSmsHistory();

  /*
   * The picker hands back only { id, dateOfBirth, title }, while the SMS form needs the
   * mobile number, the name and the deceased flag, so the chosen patient's record is
   * fetched before the form opens.
   */
  const { data: smsPatient } = usePatientData(smsPatientId);

  const tenantVal = localStorage.getItem("kinesin-tenant");

  const closeSmsModal = () => {
    setSmsPatientId(null);
    refetchSms();
  };

  const handleSmsPatientSelected = (patient) => {
    setIsPickingSmsPatient(false);
    setSmsPatientId(patient.id);
  };

  return (
    <div className={styles.wrapper} data-testid="communications-page">
      <PageHeader />

      <div className={styles.tabs}>
        <TabSwitcher
          options={TAB_OPTIONS}
          value={activeTab}
          onChange={setActiveTab}
          fullWidth
        />
      </div>

      {activeTab === TABS.SMS && (
        <PatientsSmsTable
          smsList={smsData ?? []}
          isLoading={isLoadingSms}
          handleClick={() => setIsPickingSmsPatient(true)}
          showPatientColumn
          emptyText="Messages sent from this practice will appear here."
        />
      )}

      {activeTab === TABS.EMAIL && <CommunicationsTable />}

      {activeTab === TABS.LETTERS && <LettersSection />}

      {activeTab === TABS.SICK_CERTS && <SickCertsSection />}

      <SelectPatientDialog
        open={isPickingSmsPatient}
        onClose={() => setIsPickingSmsPatient(false)}
        onSelect={handleSmsPatientSelected}
        title="Send SMS"
        description="Choose who the message is going to."
        confirmLabel="Continue"
      />

      <Dialog
        open={!!smsPatientId}
        onClose={closeSmsModal}
        title="Send sms"
        width={1000}
      >
        {smsPatient ? (
          <SmsModal2
            tenantVal={tenantVal}
            patient={smsPatient}
            onClose={closeSmsModal}
          />
        ) : (
          <Loader>Loading patient...</Loader>
        )}
      </Dialog>
    </div>
  );
};

export { CommunicationsPage };
